/**
 * useAuth — minimal auth store + API client.
 *
 * Tokens kept in localStorage (XSS surface acknowledged — CSP + careful innerHTML
 * usage required). When we move to httpOnly cookies later, only this file changes.
 *
 * Auto-refresh on 401 once per request, then bubble.
 */
import { reactive, computed } from 'vue';

const API_BASE = '/api';
const LS_ACCESS = 'mfa.access';
const LS_REFRESH = 'mfa.refresh';

const state = reactive({
  user: null,
  ready: false,           // true once we've attempted to load session on app start
  loading: false,
});

function getAccess() { return localStorage.getItem(LS_ACCESS); }
function getRefresh() { return localStorage.getItem(LS_REFRESH); }
function setTokens(pair) {
  if (pair?.access_token) localStorage.setItem(LS_ACCESS, pair.access_token);
  if (pair?.refresh_token) localStorage.setItem(LS_REFRESH, pair.refresh_token);
}
function clearTokens() {
  localStorage.removeItem(LS_ACCESS);
  localStorage.removeItem(LS_REFRESH);
}

class ApiError extends Error {
  constructor(status, body) {
    super(body?.detail || body?.message || `HTTP ${status}`);
    this.status = status;
    this.body = body;
  }
}

async function rawFetch(path, { method = 'GET', body, auth = false } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  if (auth) {
    const t = getAccess();
    if (t) headers.Authorization = `Bearer ${t}`;
  }
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body == null ? undefined : JSON.stringify(body),
  });
  let data = null;
  if (res.status !== 204) {
    const txt = await res.text();
    data = txt ? JSON.parse(txt) : null;
  }
  if (!res.ok) throw new ApiError(res.status, data);
  return data;
}

async function refreshTokens() {
  const r = getRefresh();
  if (!r) return false;
  try {
    const pair = await rawFetch('/auth/refresh', { method: 'POST', body: { refresh_token: r } });
    setTokens(pair);
    return true;
  } catch {
    clearTokens();
    return false;
  }
}

// Public api : authed fetch with 1 transparent retry on 401
async function api(path, opts = {}) {
  try {
    return await rawFetch(path, { ...opts, auth: true });
  } catch (e) {
    if (e.status === 401 && getRefresh()) {
      const ok = await refreshTokens();
      if (ok) return await rawFetch(path, { ...opts, auth: true });
    }
    throw e;
  }
}

async function fetchMe() {
  try {
    state.user = await api('/me');
  } catch {
    state.user = null;
  }
}

export async function loadSession() {
  if (state.ready) return;
  if (getAccess()) await fetchMe();
  else if (getRefresh()) {
    if (await refreshTokens()) await fetchMe();
  }
  state.ready = true;
}

export async function login(email, password) {
  state.loading = true;
  try {
    const pair = await rawFetch('/auth/login', { method: 'POST', body: { email, password } });
    setTokens(pair);
    await fetchMe();
    return state.user;
  } finally {
    state.loading = false;
  }
}

export async function register(payload) {
  state.loading = true;
  try {
    const pair = await rawFetch('/auth/register', { method: 'POST', body: payload });
    setTokens(pair);
    await fetchMe();
    return state.user;
  } finally {
    state.loading = false;
  }
}

export async function logout() {
  const r = getRefresh();
  if (r) {
    try { await rawFetch('/auth/logout', { method: 'POST', body: { refresh_token: r } }); }
    catch { /* ignore */ }
  }
  clearTokens();
  state.user = null;
}

export async function deleteAccount() {
  await api('/me', { method: 'DELETE' });
  clearTokens();
  state.user = null;
}

export function useAuth() {
  return {
    state,
    isAuthenticated: computed(() => !!state.user),
    user: computed(() => state.user),
    ready: computed(() => state.ready),
    loading: computed(() => state.loading),
    api,
    login,
    register,
    logout,
    deleteAccount,
  };
}

export { ApiError, api };
